import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useTestMode } from '../../contexts/TestModeContext';
import { filterActiveStudents } from '../../utils/studentFilters';
import PortalHeader from '../../components/Portal/PortalHeader';
import './NotesPage.css';

const NotesPage = () => {
	const [documents, setDocuments] = useState([]);
	const [students, setStudents] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchQuery, setSearchQuery] = useState('');
	const [typeFilter, setTypeFilter] = useState('all');
	const [visibilityFilter, setVisibilityFilter] = useState('all');
	const [studentFilter, setStudentFilter] = useState('all');
	const { testMode } = useTestMode();

	// Fetch documents
	useEffect(() => {
		const q = query(
			collection(db, 'documents'),
			orderBy('updatedAt', 'desc')
		);

		const unsubscribe = onSnapshot(q, (snapshot) => {
			const docs = snapshot.docs.map(doc => ({
				id: doc.id,
				...doc.data()
			}));
			setDocuments(docs);
			setLoading(false);
		}, (error) => {
			console.error('Error fetching documents:', error);
			setLoading(false);
		});

		return () => unsubscribe();
	}, []);

	// Fetch students for filter dropdown
	useEffect(() => {
		const studentsQuery = query(
			collection(db, 'students'),
			orderBy('fullName')
		);

		const unsubscribe = onSnapshot(studentsQuery, (snapshot) => {
			const allStudents = snapshot.docs.map((doc) => ({
				id: doc.id,
				...doc.data(),
			}));
			const filteredStudents = filterActiveStudents(allStudents, testMode);
			setStudents(filteredStudents);
		});

		return () => unsubscribe();
	}, [testMode]);

	const handleDelete = async (docId, e) => {
		e.preventDefault();
		e.stopPropagation();

		if (window.confirm('Are you sure you want to delete this note?')) {
			try {
				await deleteDoc(doc(db, 'documents', docId));
			} catch (error) {
				console.error('Error deleting document:', error);
			}
		}
	};

	const filteredDocuments = documents.filter(doc => {
		// Search filter
		const matchesSearch = !searchQuery ||
			doc.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
			doc.plainText?.toLowerCase().includes(searchQuery.toLowerCase());

		// Type filter
		const matchesType = typeFilter === 'all' || doc.type === typeFilter;

		// Visibility filter
		const matchesVisibility = visibilityFilter === 'all' || doc.visibility === visibilityFilter;

		// Student filter
		const matchesStudent = studentFilter === 'all' ||
			(doc.studentIds && doc.studentIds.includes(studentFilter));

		return matchesSearch && matchesType && matchesVisibility && matchesStudent;
	});

	// Get unique students that have notes linked to them
	const studentsWithNotes = students.filter(student =>
		documents.some(doc => doc.studentIds?.includes(student.id))
	);

	// Count active filters
	const activeFilterCount = [typeFilter, visibilityFilter, studentFilter]
		.filter(f => f !== 'all').length;

	const clearFilters = () => {
		setTypeFilter('all');
		setVisibilityFilter('all');
		setStudentFilter('all');
		setSearchQuery('');
	};

	const formatDate = (timestamp) => {
		if (!timestamp) return '';
		const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
		return date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric'
		});
	};

	const getPreviewText = (doc) => {
		if (doc.plainText) {
			return doc.plainText.substring(0, 120) + (doc.plainText.length > 120 ? '...' : '');
		}
		return 'No content';
	};

	const getTypeIcon = (type) => {
		switch (type) {
			case 'lesson_note': return 'fa-music';
			case 'lesson_plan': return 'fa-clipboard-list';
			case 'script': return 'fa-scroll';
			default: return 'fa-file-lines';
		}
	};

	const getTypeLabel = (type) => {
		switch (type) {
			case 'lesson_note': return 'Lesson Note';
			case 'lesson_plan': return 'Lesson Plan';
			case 'script': return 'Script';
			default: return 'Note';
		}
	};

	const getVisibilityIcon = (visibility) => {
		switch (visibility) {
			case 'student': return 'fa-user-graduate';
			case 'parent': return 'fa-user-tie';
			case 'teacher': return 'fa-chalkboard-user';
			default: return 'fa-eye';
		}
	};

	const getVisibilityLabel = (visibility) => {
		switch (visibility) {
			case 'student': return 'Student';
			case 'parent': return 'Parent';
			case 'teacher': return 'Teacher Only';
			default: return 'All';
		}
	};

	const getVisibilityColor = (visibility) => {
		switch (visibility) {
			case 'student': return '#22c55e'; // green
			case 'parent': return '#3b82f6'; // blue
			case 'teacher': return '#ef4444'; // red
			default: return '#888';
		}
	};

	return (
		<div className='notes-page'>
			<PortalHeader
				title='Lesson Recaps'
				subtitle='Recap records saved from the Lesson Dashboard'
			/>

			<div className='container py-4'>
				{/* Header Actions */}
				<div className='notes-header'>
					<div className='search-box'>
						<i className='fa-solid fa-search'></i>
						<input
							type='text'
							placeholder='Search notes...'
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
						/>
					</div>
					{/* "New Note" create flow isn't built yet — the stub button is hidden
					    rather than shown as a dead affordance. Repair-or-retire is a
					    separate card (#13 defang). */}
				</div>

				{/* Filter Bar */}
				<div className='filter-bar'>
					<div className='filter-group'>
						<select
							value={typeFilter}
							onChange={(e) => setTypeFilter(e.target.value)}
							className='filter-select'>
							<option value='all'>All Types</option>
							<option value='lesson_note'>Lesson Notes</option>
							<option value='lesson_plan'>Lesson Plans</option>
							<option value='script'>Scripts</option>
							<option value='general'>General Notes</option>
						</select>

						<select
							value={visibilityFilter}
							onChange={(e) => setVisibilityFilter(e.target.value)}
							className='filter-select'>
							<option value='all'>All Visibility</option>
							<option value='student'>Student Visible</option>
							<option value='parent'>Parent Visible</option>
							<option value='teacher'>Teacher Only</option>
						</select>

						{studentsWithNotes.length > 0 && (
							<select
								value={studentFilter}
								onChange={(e) => setStudentFilter(e.target.value)}
								className='filter-select'>
								<option value='all'>All Students</option>
								{studentsWithNotes.map(student => (
									<option key={student.id} value={student.id}>
										{student.fullName}
									</option>
								))}
							</select>
						)}
					</div>

					{activeFilterCount > 0 && (
						<button className='clear-filters-btn' onClick={clearFilters}>
							<i className='fa-solid fa-xmark'></i>
							Clear {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''}
						</button>
					)}
				</div>

				{/* Results Count */}
				<div className='results-info'>
					<span>{filteredDocuments.length} note{filteredDocuments.length !== 1 ? 's' : ''}</span>
					{(searchQuery || activeFilterCount > 0) && (
						<span className='filtered-label'> (filtered)</span>
					)}
				</div>

				{/* Documents List */}
				{loading ? (
					<div className='text-center py-5'>
						<wa-spinner></wa-spinner>
					</div>
				) : filteredDocuments.length === 0 ? (
					<div className='empty-state'>
						<i className='fa-light fa-file-lines'></i>
						<h3>No lesson recaps yet</h3>
						<p>Recaps written on the Lesson Dashboard will appear here.</p>
					</div>
				) : (
					<div className='documents-grid'>
						{filteredDocuments.map(doc => (
							<div
								key={doc.id}
								className='document-card'>
								<div className='document-card-header'>
									<span className='document-type'>
										<i className={`fa-solid ${getTypeIcon(doc.type)}`}></i>
										{getTypeLabel(doc.type)}
									</span>
									<div className='card-header-right'>
										{doc.visibility && (
											<span
												className='visibility-badge'
												style={{ '--visibility-color': getVisibilityColor(doc.visibility) }}
												title={`Visible to: ${getVisibilityLabel(doc.visibility)}`}>
												<i className={`fa-solid ${getVisibilityIcon(doc.visibility)}`}></i>
											</span>
										)}
										<button
											className='delete-btn'
											onClick={(e) => handleDelete(doc.id, e)}
											title='Delete'>
											<i className='fa-solid fa-trash'></i>
										</button>
									</div>
								</div>
								<h3 className='document-title'>
									{doc.title || 'Untitled'}
								</h3>
								<p className='document-preview'>
									{getPreviewText(doc)}
								</p>
								<div className='document-meta'>
									<span>{formatDate(doc.updatedAt)}</span>
									{doc.studentIds?.length > 0 && (
										<span className='student-badge'>
											<i className='fa-solid fa-user'></i>
											{doc.studentIds.length}
										</span>
									)}
								</div>
								</div>
						))}
					</div>
				)}
			</div>
		</div>
	);
};

export default NotesPage;
